// Source capability registry
//
// Shared UI components should ask this registry what a source supports instead of
// hard-coding Jira/Forge or ServiceNow checks inside dashboards/navigation.
// This keeps the product shell shared while allowing each source system to carry
// its own field, metrics, privacy and prompt capabilities.

export const SOURCE_SYSTEMS = Object.freeze({
  JIRA: 'jira',
  SERVICENOW: 'servicenow',
  GENERIC: 'generic',
});

export const CAPABILITY_KEYS = Object.freeze({
  EVIDENCE_SEGMENTS: 'evidenceSegments',
  ACTIVITY_METRICS: 'activityMetrics',
  SERVICE_NOW_METRICS: 'serviceNowMetrics',
  SERVICE_NOW_ANALYSIS_FIELDS: 'serviceNowAnalysisFields',
  SLA_TASK_FIELDS: 'slaTaskFields',
  SLA_METRICS: 'slaMetrics',
  CHANGE_MANAGEMENT_METRICS: 'changeManagementMetrics',
  DISCOVERY_RULES: 'discoveryRules',
  WORKBENCH: 'workbench',
  VALUE_SIMULATION: 'valueSimulation',
});

const BASE_CAPABILITIES = Object.freeze({
  evidenceSegments: true,
  activityMetrics: false,
  serviceNowMetrics: false,
  serviceNowAnalysisFields: false,
  slaTaskFields: false,
  slaMetrics: false,
  changeManagementMetrics: false,
  discoveryRules: true,
  workbench: true,
  valueSimulation: true,
});

export const SOURCE_CAPABILITY_REGISTRY = Object.freeze({
  [SOURCE_SYSTEMS.JIRA]: Object.freeze({
    ...BASE_CAPABILITIES,
    jiraWorkflowMetrics: true,
  }),
  [SOURCE_SYSTEMS.SERVICENOW]: Object.freeze({
    ...BASE_CAPABILITIES,
    // ServiceNow activity diagnostics are now consolidated into Process Metrics.
    // Keep the Activity Analysis implementation in the codebase for future use,
    // but do not expose it as an Evidence tab/capability.
    activityMetrics: false,
    serviceNowMetrics: true,
    serviceNowAnalysisFields: true,
    slaTaskFields: true,
    slaMetrics: true,
    changeManagementMetrics: true,
  }),
  [SOURCE_SYSTEMS.GENERIC]: BASE_CAPABILITIES,
});

export function normalizeSourceSystem(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'snow' || raw === 'servicenow' || raw === 'service_now') return SOURCE_SYSTEMS.SERVICENOW;
  if (raw === 'forge' || raw === 'jira' || raw === 'atlassian') return SOURCE_SYSTEMS.JIRA;
  return SOURCE_SYSTEMS.GENERIC;
}

export function getCapabilitiesForSource(sourceSystem) {
  const normalized = normalizeSourceSystem(sourceSystem);
  return SOURCE_CAPABILITY_REGISTRY[normalized] || SOURCE_CAPABILITY_REGISTRY[SOURCE_SYSTEMS.GENERIC] || BASE_CAPABILITIES;
}

export function sourceSupports(sourceSystem, capabilityKey) {
  const capabilities = getCapabilitiesForSource(sourceSystem);
  return Boolean(capabilities && capabilities[capabilityKey]);
}

export function getSourceSystemFromAppVersion(appVersion) {
  return normalizeSourceSystem(appVersion);
}

export function getEvidenceTabsForSource(sourceSystem) {
  const caps = getCapabilitiesForSource(sourceSystem);
  return [
    { id: 'metrics', label: 'Process Metrics' },
    ...(caps.slaMetrics ? [{ id: 'slaMetrics', label: 'SLA Metrics' }] : []),
    ...(caps.evidenceSegments ? [{ id: 'segments', label: 'Segments' }] : []),
    { id: 'variants', label: 'Variants' },
    { id: 'transitions', label: 'Transitions' },
    ...(caps.discoveryRules ? [
      { id: 'performance_discovery_rules', label: 'Performance Rules' },
      { id: 'compliance_discovery_rules', label: 'Compliance Rules' },
    ] : []),
  ];
}
